import { visit } from 'unist-util-visit';
/**
 * `::snippet{name="getting-help"}` — one block, written once, shown wherever
 * it is referenced.
 *
 * Variables reuse WORDS; snippets reuse BLOCKS: the support callout, the
 * prerequisite list, the "was this helpful" boilerplate. The body lives in
 * the content repo as `snippets/<name>.md`, so a snippet is drafted,
 * reviewed, published, versioned and exported exactly like a page — the
 * product adds no second storage system for content.
 *
 * Expansion happens here, in the plugin both surfaces run, from a map the
 * caller reads off its own tree — the same contract as variables, for the
 * same reason: the preview and the published page must not disagree.
 *
 * A missing snippet renders as a visible, honest placeholder rather than
 * silence: a hole a reader can see gets fixed; a hole nobody can see ships.
 * A cycle (a snippet including itself, however indirectly) stops expanding
 * at the repeated name and says so in the same placeholder shape — the
 * runtime guard, so no content can hang a build; lint reports it too.
 */
const MAX_DEPTH = 4;
/**
 * Open/close html nodes with a REAL paragraph between, the same shape every
 * directive renderer here uses: text inside a single raw html string was
 * stripped by the sanitising pipeline, while mdast children survive it.
 */
const placeholder = (text) => [
    { type: 'html', value: '<div class="sls-snippet-missing">' },
    { type: 'paragraph', children: [{ type: 'text', value: text }] },
    { type: 'html', value: '</div>' },
];
export function expandSnippets(tree, deps) {
    expandInto(tree, deps, []);
}
function expandInto(tree, deps, stack) {
    visit(tree, 'leafDirective', (node, index, parent) => {
        if (node.name !== 'snippet' || parent === undefined || index === undefined)
            return;
        const name = String(node.attributes?.['name'] ?? '').trim();
        const replacement = (() => {
            if (!name)
                return placeholder('This snippet reference names no snippet.');
            if (stack.includes(name) || stack.length >= MAX_DEPTH) {
                return placeholder(`Snippet "${name}" includes itself — the loop stops here.`);
            }
            const body = deps.snippets[name];
            if (body === undefined) {
                return placeholder(`Missing snippet "${name}" — it was deleted or never created.`);
            }
            const inner = deps.parse(body);
            expandInto(inner, deps, [...stack, name]);
            return inner.children;
        })();
        parent.children.splice(index, 1, ...replacement);
        // Re-visit the spliced region: an expanded snippet may itself have been
        // replaced by multiple nodes; continuing at the same index covers them.
        return index;
    });
}
/** `snippets/getting-help.md` -> `getting-help`; null when not a snippet path. */
export function snippetNameOfPath(path) {
    const m = /^snippets\/([a-z][a-z0-9-]*)\.md$/.exec(path);
    return m ? m[1] : null;
}
/**
 * A tree's shared blocks, keyed by name, with their frontmatter stripped.
 *
 * A snippet file may carry frontmatter, and what it contributes to a page is
 * its CONTENT, not its metadata. Two callers need this and they must not
 * disagree: the renderer, which splices the body into every page that includes
 * it, and the linter's anchor check, which counts a heading inside a shared
 * block as a heading on every one of those pages.
 *
 * It lives here rather than beside the workspace because it is a fact about
 * markdown files, not about repositories or databases — and the CLI needs it
 * without any of the machinery a workspace brings.
 */
export function snippetBodiesOf(files) {
    const bodies = {};
    for (const [path, body] of files) {
        const name = snippetNameOfPath(path);
        if (name === null)
            continue;
        bodies[name] = body.replace(/^---\n[\s\S]*?\n---\n/, '').replace(/^\n+/, '');
    }
    return bodies;
}
/** Every snippet name a page body references, for lint's undefined check. */
export function snippetReferences(tree) {
    const names = new Set();
    visit(tree, 'leafDirective', (node) => {
        if (node.name !== 'snippet')
            return;
        const name = String(node.attributes?.['name'] ?? '').trim();
        if (name)
            names.add(name);
    });
    return names;
}
//# sourceMappingURL=snippets.js.map